import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { getChangeRequestParticipants, setChangeRequestParticipants } from '@/services/change-requests';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import ChangeRequestParticipantSelectList, {
    ChangeRequestParticipantSelectListItem,
} from './ChangeRequestParticipantSelectList';
import { YesNoDialog } from '@/components/dialogs';
import Banner from '@/components/Banner';

interface ChangeRequestParticipantsDialogProps {
    projectId: number;
    changeRequestId: number;
    show: boolean;
    onClose(): void;
    onSave(participants: ChangeRequestParticipantSelectListItem[]): void;
}

//TODO: switch to ChangeRequestParticipantSelectList
const ChangeRequestParticipantsDialog: React.FC<ChangeRequestParticipantsDialogProps> = ({
    projectId,
    changeRequestId,
    show,
    onClose,
    onSave,
}) => {
    const [saving, setSaving] = useState(false);
    const [selection, setSelection] = useState<ChangeRequestParticipantSelectListItem[] | null>(null);
    const [participantsWithOffer, setParticipantsWithOffer] = useState<number[] | null>(null);
    const [showConfirmRemoveParticipantModal, setShowConfirmRemoveParticipantModal] = useState(false);

    const onOpen = async () => {
        setSelection(null);
    };

    const onOpened = async () => {
        var result = await getChangeRequestParticipants(changeRequestId);
        setParticipantsWithOffer(result.filter((x) => x.offerFinalPrice !== null).map((x) => x.userId));
        setSelection(result);
    };

    const save = async () => {
        const data = selection!.map((item, index) => ({ ...item, priority: index + 1 }));

        setSaving(true);

        try {
            await setChangeRequestParticipants(changeRequestId, data);
            triggerHandler(onSave, selection!);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };
    const handleSaveClick = () => {
        const selectionUserIds = selection?.map((x) => x.userId);

        if (participantsWithOffer?.some((pwo) => !selectionUserIds?.some((x) => x === pwo))) {
            // A participant with an offer has been removed
            setShowConfirmRemoveParticipantModal(true);
        } else {
            save();
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <>
            <Modal
                isOpen={show}
                onOpened={onOpened}
                onOpen={onOpen}
                toggle={handleCancelClick}
                size="lg">
                <ModalHeader>
                    <Trans>Add / remove participants</Trans>
                </ModalHeader>
                <ModalBody>
                    {selection ? (
                        <ChangeRequestParticipantSelectList
                            key={changeRequestId}
                            projectId={projectId}
                            value={selection}
                            onChange={setSelection}
                            participantsIdsWithOffer={participantsWithOffer}
                        />
                    ) : (
                        <LoadingIndicator />
                    )}
                </ModalBody>
                <ModalFooter>
                    <ButtonBar margins="none">
                        <ButtonBar.Right>
                            <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button variant="primary" onClick={handleSaveClick} loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ModalFooter>
            </Modal>
            <YesNoDialog
                onClose={() => setShowConfirmRemoveParticipantModal(false)}
                show={showConfirmRemoveParticipantModal}
                onAccept={save}
                confirmButtonColor="danger"
                confirmButtonText={<Trans>Remove</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                title={<Trans>Remove participant</Trans>}
                body={
                    <>
                        <Trans>Are you sure you would like to remove this user?</Trans>
                        <Banner type="warning">
                            <Trans>
                                <strong>Please note</strong> removing this participant, will mean that their offer will be
                                removed as well.
                            </Trans>
                        </Banner>
                    </>
                }
            />
        </>
    );
};

export default ChangeRequestParticipantsDialog;